import React, {createContext, ReactNode, useCallback, useContext, useState} from 'react';
import {Alert, AlertColor, Slide, Snackbar, SnackbarOrigin} from '@mui/material';

type SnackbarDuration = number | null | undefined;

/**
 * Shows a snackbar message with the specified severity and duration.
 *
 * @param {string} message - The message to display in the snackbar.
 * @param {AlertColor} [severity='success'] - The severity level of the snackbar (e.g., 'success', 'error', 'warning', 'info').
 * @param {SnackbarDuration} [duration=8000] - The duration (in milliseconds) for which the snackbar should be visible. If null or undefined, the snackbar will remain open until manually closed.
 * @param {SnackbarOrigin} [position] - The position on the screen where the snackbar should appear. If not provided, it defaults to { vertical: "top", horizontal: "center" }.
 * @returns {void}
 */
export type SnackbarShowMessage = (message: string, severity: AlertColor, duration?: SnackbarDuration, position?: SnackbarOrigin) => void;

interface SnackbarContextValue {
  snackbarShowMessage: SnackbarShowMessage;
}

const SnackbarContext = createContext<SnackbarContextValue | undefined>(undefined);

SnackbarContext.displayName = 'SnackbarContext';

/**
 * Provides a single app-level Snackbar instance accessible via {@link useSnackbar}.
 * Must be rendered inside MUI's ThemeProvider so the Snackbar inherits the theme.
 *
 * @example
 * <ThemeProvider theme={globalThemes}>
 *   <SnackbarProvider>
 *     <App />
 *   </SnackbarProvider>
 * </ThemeProvider>
 */
export const SnackbarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('An Error Occurred');
  const [duration, setDuration] = useState<SnackbarDuration>(8000);
  const [severity, setSeverity] = useState<AlertColor>('success');
  const [anchorOrigin, setAnchorOrigin] = useState<SnackbarOrigin>({ vertical: 'top', horizontal: 'center' });

  const showMessage = useCallback<SnackbarShowMessage>((message, severity = 'success', duration = 8000, position?) => {
    setMessage(message ?? 'Something went wrong!');
    setSeverity(severity);
    setDuration(duration);
    if (position) {
      setAnchorOrigin(position);
    }
    setOpen(true);
  }, []);

  return (
    <SnackbarContext.Provider value={{ snackbarShowMessage: showMessage }}>
      {children}
      <Snackbar
        anchorOrigin={anchorOrigin}
        autoHideDuration={duration}
        open={open}
        onClose={() => setOpen(false)}
        TransitionComponent={Slide}
        className="snackbar"
        sx={{
          '& .MuiAlert-message, .MuiAlert-icon, .MuiAlert-action': {
            color: '#fff',
          },
        }}
      >
        <Alert variant="filled" onClose={() => setOpen(false)} severity={severity}>
          {message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

/**
 * Returns the snackbar `showMessage` function from the nearest {@link SnackbarProvider}.
 *
 * @throws {Error} If called outside a SnackbarProvider.
 *
 * @example
 * const { showMessage } = useSnackbar();
 * showMessage('Saved successfully!', 'success');
 * showMessage('Something went wrong.', 'error', 10000);
 */
export function useSnackbar(): SnackbarContextValue {
  const ctx = useContext(SnackbarContext);
  if (!ctx) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return ctx;
}

